import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { 
  Bell, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye,
  XCircle,
  CheckCircle,
  Clock,
  AlertTriangle,
  ExternalLink
} from 'lucide-react'
import { SkeletonTable } from '../components/Skeleton'
import toast from 'react-hot-toast'

const Updates = () => {
  const [updates, setUpdates] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(null)
  const { getUpdates, deleteUpdate, sendUpdate } = useFirebase()

  useEffect(() => {
    loadUpdates()
  }, [])

  const loadUpdates = async () => {
    try {
      setLoading(true)
      const data = await getUpdates()
      setUpdates(data || [])
    } catch (error) {
      console.error('Error loading alerts:', error)
      toast.error('Failed to load alerts')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = (update) => {
    setConfirmDelete(update)
  }

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return
    try {
      await deleteUpdate(confirmDelete.id)
      await loadUpdates()
      toast.success('Alert deleted successfully')
    } catch (error) {
      console.error('Error deleting alert:', error)
      toast.error('Failed to delete alert')
    } finally {
      setConfirmDelete(null)
    }
  }

  const cancelDelete = () => {
    setConfirmDelete(null)
  }

  const handleSendNow = async (id) => {
    if (window.confirm('Are you sure you want to send this alert to all users now?')) {
      try {
        await sendUpdate(id)
        setUpdates(updates.map(update => 
          update.id === id 
            ? { ...update, status: 'sent', sentAt: new Date().toISOString() }
            : update
        ))
        toast.success('Alert sent successfully')
      } catch (error) {
        console.error('Error sending alert:', error)
        toast.error('Failed to send alert')
      }
    }
  }

  const formatDMY = (date) => {
    if (!date) return 'N/A'
    try {
      const d = date?.toDate ? date.toDate() : new Date(date)
      if (isNaN(d)) return 'N/A'
      const dd = String(d.getDate()).padStart(2, '0')
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const yyyy = d.getFullYear()
      return `${dd}/${mm}/${yyyy}`
    } catch {
      return 'N/A'
    }
  }



  const filteredUpdates = updates.filter(update => {
    const matchesSearch = update.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         update.message?.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesSearch
  })

  const getTypeIcon = (type) => {
    switch (type) {
      case 'announcement':
        return <Megaphone className="h-4 w-4" />
      case 'question':
        return <Bell className="h-4 w-4" />
      case 'feature':
        return <Target className="h-4 w-4" />
      case 'maintenance':
        return <AlertTriangle className="h-4 w-4" />
      default:
        return <Bell className="h-4 w-4" />
    }
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'announcement':
        return 'bg-blue-100 text-blue-800'
      case 'question':
        return 'bg-green-100 text-green-800'
      case 'feature':
        return 'bg-purple-100 text-purple-800'
      case 'maintenance':
        return 'bg-orange-100 text-orange-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'scheduled':
        return <Clock className="h-4 w-4 text-blue-600" />
      case 'draft':
        return <Edit className="h-4 w-4 text-yellow-600" />
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />
      default:
        return <Clock className="h-4 w-4 text-gray-600" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800'
      case 'scheduled':
        return 'bg-blue-100 text-blue-800'
      case 'draft':
        return 'bg-yellow-100 text-yellow-800'
      case 'failed':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl shadow-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2 flex items-center">
              <Bell className="h-8 w-8 mr-3" />
              Alerts
            </h1>
          </div>
          <div className="flex items-center space-x-3">
            <Link to="/updates/add" className="btn-white">
              <Plus className="h-5 w-5 mr-2" />
              Create Alert
            </Link>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search alerts by title or message..."
            className="input pl-10 w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Updates List */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            Alerts ({filteredUpdates.length})
          </h3>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <SkeletonTable rows={8} columns={4} />
          ) : filteredUpdates.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Alerts Details</th>
                    <th>Link</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUpdates.map((update) => (
                    <tr key={update.id} className="hover:bg-gray-50">
                      <td>
                        <div className="flex items-start space-x-3">
                          {update.imageUrl ? (
                            <img 
                              src={update.imageUrl} 
                              alt={update.title}
                              className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                              onError={(e) => {
                                e.target.style.display = 'none'
                                e.target.nextSibling.style.display = 'flex'
                              }}
                            />
                          ) : null}
                          <div className={`w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 ${update.imageUrl ? 'hidden' : ''}`}>
                            <Bell className="h-5 w-5 text-green-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="font-medium text-gray-900 truncate">
                              {update.title}
                            </h4>
                            <p className="text-sm text-gray-600 truncate max-w-xs">
                              {update.message}
                            </p>

                          </div>
                        </div>
                      </td>
                      <td>
                        {update.link ? (
                          <a 
                            href={update.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm"
                            title={update.link}
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            <span className="truncate max-w-32">
                              {update.link.length > 30 ? `${update.link.substring(0, 30)}...` : update.link}
                            </span>
                          </a>
                        ) : (
                          <span className="text-gray-400 text-sm">No link</span>
                        )}
                      </td>
                      <td className="text-sm text-gray-600">
                        {update.status === 'sent' && update.sentAt
                          ? formatDMY(update.sentAt)
                          : update.status === 'scheduled' && update.scheduledAt
                          ? formatDMY(update.scheduledAt)
                          : update.createdAt
                          ? formatDMY(update.createdAt)
                          : 'N/A'
                        }
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/updates/view/${update.id}`}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                          <Link
                            to={`/updates/edit/${update.id}`}
                            className="text-green-600 hover:text-green-900"
                            title="Edit"
                            onClick={() => toast.success('Opening edit page...')}
                          >
                            <Edit className="h-4 w-4" />
                          </Link>
                          <button
                            onClick={() => handleDelete(update)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Alert Found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm
                  ? 'Try adjusting your search criteria'
                  : 'Get started by creating your first alert'
                }
              </p>
              {!searchTerm && (
                <Link to="/updates/add" className="btn-primary">
                  <Plus className="h-5 w-5 mr-2" />
                  Create First Alert
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={cancelDelete} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="text-lg font-semibold text-gray-900 mb-2">Delete Alert?</div>
            <div className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete "{confirmDelete.title}"? This action cannot be undone.
            </div>
            <div className="flex justify-end space-x-2">
              <button onClick={cancelDelete} className="btn-outline">Cancel</button>
              <button onClick={confirmDeleteAction} className="btn-danger">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Updates
